"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports._testHooks = exports.default = exports.baseNextState = exports.loadScript = void 0;

var _react = _interopRequireWildcard(require("react"));

var _propTypes = _interopRequireDefault(require("prop-types"));

var _isLocalStorageSupported = require("@zillow/is-local-storage-supported");

var _get2 = _interopRequireDefault(require("lodash/get"));

var _ConstantDiv = _interopRequireDefault(require("./util/ConstantDiv"));

var _mapPositionUtils = require("./util/mapPositionUtils");

var _ZillowMapContext = _interopRequireDefault(require("./ZillowMapContext"));

var _MapStyleDefinitions = _interopRequireDefault(require("./MapStyleDefinitions"));

var _constants = require("./util/constants");

var _jsxFileName = "/builds/zillow/searchxp/zillow-map-control/src/ZillowMapControl.jsx";

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }

function _interopRequireWildcard(obj) { if (obj && obj.__esModule) { return obj; } else { var newObj = {}; if (obj != null) { for (var key in obj) { if (Object.prototype.hasOwnProperty.call(obj, key)) { var desc = Object.defineProperty && Object.getOwnPropertyDescriptor ? Object.getOwnPropertyDescriptor(obj, key) : {}; if (desc.get || desc.set) { Object.defineProperty(newObj, key, desc); } else { newObj[key] = obj[key]; } } } } newObj.default = obj; return newObj; } }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; var ownKeys = Object.keys(source); if (typeof Object.getOwnPropertySymbols === 'function') { ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function (sym) { return Object.getOwnPropertyDescriptor(source, sym).enumerable; })); } ownKeys.forEach(function (key) { _defineProperty(target, key, source[key]); }); } return target; }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

function _assertThisInitialized(self) { if (self === void 0) { throw new ReferenceError("this hasn't been initialised - super() hasn't been called"); } return self; }

function _inheritsLoose(subClass, superClass) { subClass.prototype = Object.create(superClass.prototype); subClass.prototype.constructor = subClass; subClass.__proto__ = superClass; }

// To avoid floating point errors with fine decimal values,
// at some times latitudes and longitudes are mulitiplied by 1 million to create whole number values.
var fromMillionths = function fromMillionths(v) {
  if (Math.abs(v) > 1000) {
    return v / 1000000;
  }

  return v;
};

var canUseDOM = function canUseDOM() {
  return typeof window !== 'undefined';
}; // Each time a new map instance is needed, check for a cached instance that can be reused.
// Each time a new map instance gets umounted, save it for reuse.


var mapInstanceCache = {
  instances: [// {  Example:
    //    mapDiv: 'div', // Map instances don't have a setDiv method.  So the div also has to be preserved past the normal end of the React lifecycle.
    //    mapInstance: 'instance', // Google instance of the map.
    // }
  ],
  loadFinished: false,
  loadStarted: false,
  version: 1
};

if (canUseDOM()) {
  /* eslint-disable */
  // using underscore global to share cache between all map instances
  // and make it clear this is a private variable.
  if (window._sharedMapInstanceCache) {
    // Check if global mapInstanceCache is a compatible version
    // If not, don't use it to avoid crashing (at the expense of a memory leak)
    // and throw an error so devs can fix it.
    // This likely means the format for _sharedMapInstanceCache has changed in
    // another version of zillow map control on the page, or in the early pre-loader
    if (window._sharedMapInstanceCache.version === mapInstanceCache.version) {
      mapInstanceCache = window._sharedMapInstanceCache;
    } else {
      // Throwing exception on timeout so it can be caught and reported, but not interupt page load.
      window.setTimeout(function () {
        throw "ZillowMapControl: Bad Shared Map Instance";
      }, 0);
    }
  } else {
    window._sharedMapInstanceCache = mapInstanceCache;
  }
  /* eslint-enable */

} // Method to reset the instances for unit tests.
// actual maps are not loaded in unit tests, so this doesn't waste actual api calls.


var resetInstanceCache = function resetInstanceCache() {
  mapInstanceCache = {
    instances: [],
    loadFinished: false,
    loadStarted: false
  };
  /* eslint-disable */

  window._sharedMapInstanceCache = [];
  /* eslint-enable */
}; // ZillowMapControl is the primary component object to render a google map.
// Users are expected to supply their own UI controls for zoom in and out or other manipulators.
// ZillowMapControl works in tandom with ZillowMapLayer to position content on the map.
// Example:
// <ZillowMapControl>
// .   <ZillowMapLayer>
//        <Components_With_Cordinate_Properties/>
// .   </ZillowMapLayer>
// .   <User_UI_Controls/>
// </ZillowMapControl>
// Interval to sample waiting for the load event
// This is an arbitrary number.


var GOOGLE_LOAD_SAMPLE_RATE = 100;

function waitForGoogleLoad() {
  return new Promise(function (resolve) {
    function check(checkResolve) {
      // If the load has completed, resolve the waiting promise.
      if (mapInstanceCache.loadFinished) {
        checkResolve();
      } else {
        // Else wait some more.
        setTimeout(check.bind(null, checkResolve), GOOGLE_LOAD_SAMPLE_RATE);
      }
    } // Check back later in sample time ms.


    setTimeout(check.bind(null, resolve), GOOGLE_LOAD_SAMPLE_RATE);
  });
}

var loadScript = function loadScript(src) {
  // If this method ever gets called server side, skip attempting the load.
  if (!canUseDOM()) {
    // eslint-disable-next-line no-console
    console.error("Can't load google map server side.  Rejecting promise.");
    return Promise.reject();
  } // If this is the first attempt to load the map.


  if (!mapInstanceCache.loadStarted) {
    // Mark the map load in started
    mapInstanceCache.loadStarted = true; // Add a script tag to start loading the map.

    return new Promise(function (resolve, reject) {
      var s = document.createElement('script');
      s.src = src;

      s.onload = function () {
        mapInstanceCache.loadFinished = true;
        resolve();
      };

      s.onerror = reject;
      document.head.appendChild(s);
    });
  }

  if (mapInstanceCache.loadFinished) {
    // If a previous load attempt has already finished,
    // then loading is complete, so resolve the promise.
    return Promise.resolve();
  } // Loading has started, but hasn't completed yet.
  // Shouldn't start another load attempt, but
  // can't resolve the promise yet either.


  return waitForGoogleLoad();
}; // Base state to begin with when applying new prop changes and syncing the gmap.


exports.loadScript = loadScript;
var baseNextState = {
  mapCenter: null,
  zoomLevel: null,
  mapType: null,
  boundingRect: null,
  gestureHandling: null
};
exports.baseNextState = baseNextState;

var ZillowMapControl =
/*#__PURE__*/
function (_Component) {
  _inheritsLoose(ZillowMapControl, _Component);

  ZillowMapControl.googleFail = function googleFail(reason) {
    throw new Error("Google map failed to initialize. Reason: " + reason);
  };

  ZillowMapControl.boundsMinimumDelta = function boundsMinimumDelta(currentBounds, newBounds) {
    var MIN_DELTA = 0.0001;

    if (!currentBounds || !newBounds || currentBounds.north === undefined) {
      return true;
    }

    return Math.abs(currentBounds.north - newBounds.north) > MIN_DELTA || Math.abs(currentBounds.south - newBounds.south) > MIN_DELTA || Math.abs(currentBounds.east - newBounds.east) > MIN_DELTA || Math.abs(currentBounds.west - newBounds.west) > MIN_DELTA;
  };

  ZillowMapControl.boundRectMinimumDelta = function boundRectMinimumDelta(currentBounds, newBounds) {
    var MIN_DELTA = 0.0001;

    if (!currentBounds || !newBounds || currentBounds.northLat === undefined) {
      return true;
    }

    return Math.abs(currentBounds.northLat - newBounds.northLat) > MIN_DELTA || Math.abs(currentBounds.southLat - newBounds.southLat) > MIN_DELTA || Math.abs(currentBounds.eastLng - newBounds.eastLng) > MIN_DELTA || Math.abs(currentBounds.westLng - newBounds.westLng) > MIN_DELTA;
  } // Helper to make evaluating prop changes more readable.
  ;

  ZillowMapControl.isNewProp = function isNewProp(nextProps, baseProps, propName) {
    return nextProps[propName] !== baseProps[propName];
  };

  function ZillowMapControl(props) {
    var _this;

    _this = _Component.call(this, props) || this;
    _this.state = {
      mapStyleClass: _constants.STREET_MAP_CLASS,
      lastReportedBounds: {}
    };
    _this.googleReady = _this.googleReady.bind(_assertThisInitialized(_this));
    _this.registerChild = _this.registerChild.bind(_assertThisInitialized(_this));
    _this.gmap = null;
    _this.mapDiv = null;
    _this.onBoundsChange = _this.onBoundsChange.bind(_assertThisInitialized(_this));
    _this.onDragStart = _this.onDragStart.bind(_assertThisInitialized(_this));
    _this.onClick = _this.onClick.bind(_assertThisInitialized(_this));
    _this.onIdle = _this.onIdle.bind(_assertThisInitialized(_this));
    _this.onMapTypeIdChanged = _this.onMapTypeIdChanged.bind(_assertThisInitialized(_this));
    _this.onZoomChanged = _this.onZoomChanged.bind(_assertThisInitialized(_this));
    _this.onStreetViewChanged = _this.onStreetViewChanged.bind(_assertThisInitialized(_this));
    _this.logEvent = _this.logEvent.bind(_assertThisInitialized(_this));
    _this.registerHandlers = _this.registerHandlers.bind(_assertThisInitialized(_this));
    _this.heavyEventTracking = false;
    _this.registeredListeners = [];
    _this.mapStyles = _MapStyleDefinitions.default;
    _this.isMapInvisible = false;

    if (canUseDOM()) {
      var mapStyleOverride = (0, _isLocalStorageSupported.isLocalStorageSupported)() && window.localStorage.getItem('mapConfig');

      if (mapStyleOverride) {
        try {
          _this.mapStyles = JSON.parse(mapStyleOverride);
        } catch (er) {
          // eslint-disable-next-line no-console
          console.error('ZillowMapControl: Could not parse mapConfig for style overrides');
        }
      }
    }

    return _this;
  }

  var _proto = ZillowMapControl.prototype;

  _proto.componentDidMount = function componentDidMount() {
    var _this$props = this.props,
        apiKey = _this$props.apiKey,
        useBeta = _this$props.useBeta,
        additionalLibraries = _this$props.additionalLibraries;

    if (canUseDOM()) {
      var scriptURL = "https://maps.googleapis.com/maps/api/js?key=" + apiKey;

      if (additionalLibraries && additionalLibraries.length > 0) {
        scriptURL += "&libraries=" + additionalLibraries.join(',');
      }

      if (useBeta) {
        scriptURL += '&v=3.exp&use_slippy=true';
      } else {
        scriptURL += '&v=3.45';
      }

      loadScript(scriptURL).then(this.googleReady, ZillowMapControl.googleFail);
    }
  } // This used to be componentWillUpdate, but that method is being deprecated.
  // This compares the previous props with the new props to look for changes.
  ;

  _proto.componentDidUpdate = function componentDidUpdate(previousProps) {
    this.applyPropChanges(this.props, previousProps, false);
  };

  _proto.componentWillUnmount = function componentWillUnmount() {
    if (this.constDiv && this.mapDiv && this.gmap) {
      this.constDiv.removeChild(this.mapDiv);
      mapInstanceCache.instances.push({
        mapInstance: this.gmap,
        mapDiv: this.mapDiv
      });
      this.removeListeners();
      this.gmap = null;
      this.mapDiv = null;
    }
  };

  _proto.onZoomChanged = function onZoomChanged(ev) {
    if (this.supressMapEventHandling) {
      return;
    }

    this.logEvent('onZoomChanged', ev);

    if (this.gmap && this.props.onZoomChanged) {
      this.props.onZoomChanged(this.gmap.getZoom());
    }
  };

  _proto.onStreetViewChanged = function onStreetViewChanged(ev) {
    if (this.supressMapEventHandling) {
      return;
    }

    this.logEvent('onStreetViewChanged', ev);

    if (this.gmap && this.props.onStreetViewChanged) {
      // Calls onStreetViewChanged prop with `true` if the street view has been opened.
      // Calls onStreetViewCHanged prop with `false` if the street view has been closed.
      this.props.onStreetViewChanged(!!this.gmap.getStreetView().getVisible());
    }
  };

  _proto.onBoundsChange = function onBoundsChange(ev) {
    if (this.supressMapEventHandling) {
      return;
    }

    this.logEvent('onBoundsChange', ev); // flag bounds have changed and fire on idle

    this.boundsChanged = true;
  };

  _proto.onMapTypeIdChanged = function onMapTypeIdChanged(ev) {
    if (this.supressMapEventHandling) {
      return;
    }

    this.logEvent('_onMapTypeIdChanged', ev);

    if (this.props.onMapTypeIdChanged && this.gmap) {
      switch (this.gmap.getMapTypeId()) {
        case 'roadmap':
          this.props.onMapTypeIdChanged('road');
          return;

        case 'satellite':
          this.props.onMapTypeIdChanged('aerial');
          return;

        case 'hybrid':
          this.props.onMapTypeIdChanged('aerial');
          return;

        case 'terrain':
          this.props.onMapTypeIdChanged('aerial');
          return;

        default:
          this.props.onMapTypeIdChanged('road');
      }
    }
  };

  _proto.onIdle = function onIdle(ev) {
    var _this$props2 = this.props,
        onReady = _this$props2.onReady,
        onBoundsChange = _this$props2.onBoundsChange;

    if (this.state.googleReady && !this.state.googleReadyFired && onReady) {
      this.setState({
        googleReadyFired: true
      });
      onReady(this.gmap.getProjection(), this.gmap.zoom);
    } // If map was set to invisible because a cached version was reused and therefore
    //   had wrong image, idle indicates it should be clear and ready for display now


    this.showMapIfInvisible();

    if (this.supressMapEventHandling) {
      return;
    }

    this.logEvent('_onIdle', ev); // There is a race condition on reused instances of ZMC where
    // gmap can exist, but not have bounds yet.  So check bounds before using

    if (this.boundsChanged && this.gmap && this.gmap.getBounds()) {
      this.logEvent('_onMapSettle', ev);
      var mapBounds = this.gmap.getBounds();
      var newBounds = {
        northLat: mapBounds.getNorthEast().lat(),
        southLat: mapBounds.getSouthWest().lat(),
        eastLng: mapBounds.getNorthEast().lng(),
        westLng: mapBounds.getSouthWest().lng()
      };
      var validNewBounds = newBounds.northLat !== newBounds.southLat && newBounds.eastLng !== newBounds.westLng;
      var mapCenter = this.gmap.getCenter();
      var newCenter = {
        northSouthLat: mapCenter.lat(),
        eastWestLng: mapCenter.lng()
      };
      var newZoom = this.gmap.getZoom();
      var projection = this.gmap.getProjection();
      var bounds = this.gmap.getBounds();
      var topRight = projection.fromLatLngToPoint(bounds.getNorthEast());
      var bottomLeft = projection.fromLatLngToPoint(bounds.getSouthWest());
      var boundsEvent = {
        bounds: newBounds,
        center: newCenter,
        tileBounds: {
          left: bottomLeft.x,
          right: topRight.x,
          top: topRight.y,
          bottom: bottomLeft.y
        },
        zoom: newZoom,
        firstLoad: this.firstLoad
      };
      this.setState({
        lastReportedBounds: boundsEvent
      });

      if (onBoundsChange && validNewBounds) {
        onBoundsChange(boundsEvent);
      }
    }

    this.firstLoad = false;
    this.boundsChanged = false;
  };

  _proto.onDragStart = function onDragStart(ev) {
    if (this.supressMapEventHandling) {
      return;
    }

    this.logEvent('onDragStart', ev);
    this.zoomAtStartOfPan = this.gmap ? this.gmap.getZoom() : 3;

    if (this.props.onDragStart) {
      this.props.onDragStart();
    }
  };

  _proto.onClick = function onClick(ev) {
    var mapBoundingRect = this.mapDiv.getBoundingClientRect(); // Get the X, Y coordinates of the click relative to the map

    var mapX = ev.clientX - mapBoundingRect.left;
    var mapY = ev.clientY - mapBoundingRect.top;
    this.logEvent('onClick', ev);
    var latLng = (0, _mapPositionUtils.convertScreenXYToLatLng)(mapX, mapY, this.gmap);
    this.props.onClickMapAnywhere(ev, {
      northSouthLat: latLng.lat(),
      eastWestLng: latLng.lng()
    });
  };

  _proto.registerChild = function registerChild(child) {
    this.constDiv = child;

    if (this.gmap && this.constDiv) {
      // Its possible the map was ready before hydration finished
      // so recover by appending the mapDiv here.
      this.constDiv.appendChild(this.mapDiv);
    }
  };

  _proto.generateUnusedGoogleEventLogFunction = function generateUnusedGoogleEventLogFunction(name) {
    var _this2 = this;

    return function () {
      _this2.logEvent(name, null, 'event fired from google map');
    };
  };

  _proto.logEvent = function logEvent(eventName, event, message, force) {
    if (this.heavyEventTracking || force) {
      var eventData = event ? event.toString() : 'null';

      if (event) {
        try {
          eventData = JSON.stringify(event);
        } catch (err) {// object failed to convert to JSON smoothly
        }
      }

      var consoleMessage = "ZMC:" + eventName + ":" + eventData;

      if (message) {
        consoleMessage += ":" + message;
      } // eslint-disable-next-line no-console


      console.log(consoleMessage);

      if (canUseDOM() && window.zonLogString) {
        window.zonLogString += consoleMessage;
        window.zonLogString += '\n';
      }
    }
  };

  _proto.googleReady = function googleReady() {
    var _this3 = this;

    var _this$props3 = this.props,
        zoomLevel = _this$props3.zoomLevel,
        maxZoom = _this$props3.maxZoom,
        minZoom = _this$props3.minZoom,
        streetViewEnabled = _this$props3.streetViewEnabled; // mapCenter has a default, but it's also legit for the user to pass in nothing.
    // however we need to give google map a starting location or it won't start over the US.
    // same for zoomLevel.

    var mapCenter = this.props.mapCenter || _constants.DEFAULT_MAP_CENTER;
    this.setState({
      googleReady: true,
      googleReadyFired: false
    }); // Custom logic specific to search results page.
    // This allows search results page to pre-init a map before page javascript loads

    if (window.searchMapDiv) {
      this.mapDiv = window.searchMapDiv;
      this.gmap = window.searchGMap;

      if (this.constDiv) {
        if (window.searchMapDiv.parentElement) {
          window.searchMapDiv.parentElement.removeChild(window.searchMapDiv);
        }

        this.constDiv.appendChild(window.searchMapDiv);
      } // Need to force a bounds change event to enable
      // getting initial map results.  The one that occurs naturally
      // may have fired before picking up the preloaded map instance.


      this.fireFirstBoundsChange = true; // Clean up global references so a secondary map instance does not attempt to reuse.

      window.searchMapDiv = null;
      window.searchGMap = null;
    } else if (mapInstanceCache.instances.length > 0) {
      this.mapDiv = mapInstanceCache.instances[0].mapDiv;
      this.gmap = mapInstanceCache.instances[0].mapInstance;
      mapInstanceCache.instances.shift(); // Turn off street view, because otherwise if the cached map was closed while in street view,
      // the next map that opens will start in street view

      this.gmap.getStreetView().setVisible(false); // its possible that google is ready before the map is ready due to
      // aggressive pre-loading of map tools.  So wait until constDiv registers
      // before mounting.

      if (this.constDiv) {
        // Reusing a cached map will show the previous image briefly, so we make it invisible
        //   until that image has been cleared (wait for onIdle)
        this.mapDiv.style.visibility = 'hidden';
        this.isMapInvisible = true;
        this.constDiv.appendChild(this.mapDiv); // Safeguard to ensure map eventually displays in case onIdle isn't triggered

        setTimeout(function () {
          _this3.showMapIfInvisible();
        }, 1000);
      }

      google.maps.event.trigger(this.gmap, "resize");
    } else if (canUseDOM()) {
      this.mapDiv = document.createElement('div');
      this.mapDiv.style.position = 'absolute';
      this.mapDiv.style.width = '100%';
      this.mapDiv.style.height = '100%';

      if (this.constDiv) {
        // its possible that google is ready before map is ready due to
        // agressive pre-loading of map tools.  So wait until cosntDiv registers
        // before mounting.
        this.constDiv.appendChild(this.mapDiv);
      } // eslint-disable-next-line no-undef


      this.gmap = new google.maps.Map(this.mapDiv, {
        center: {
          lat: mapCenter.northSouthLat,
          lng: mapCenter.eastWestLng
        },
        restriction: {
          // prevent wrapping around the 180th meridian
          latLngBounds: {
            north: 90,
            south: -90,
            west: -179.999,
            east: 180
          },
          strictBounds: true
        },
        zoom: zoomLevel || _constants.DEFAULT_ZOOM,
        clickableIcons: false,
        disableDefaultUI: true,
        maxZoom: maxZoom,
        minZoom: minZoom,
        tilt: _constants.DEFAULT_TILT,
        mapTypeControl: false,
        zoomControl: false,
        fullscreenControl: false,
        panControl: false,
        rotateControl: false,
        scaleControl: false,
        gestureHandling: this.props.gestureHandling,
        streetViewControl: !!streetViewEnabled,
        styles: this.mapStyles,
        // noPerTile allows google to skip a network cycle for latest tile, at the cost of getting a tile upto a week old.
        noPerTile: true
      });
    }

    this.registerHandlers(); // Apply the prop state to the map.
    // Since this will be the first time the map can receive props
    // the base value is empty so all props are considered 'new'

    this.applyPropChanges(this.props, {}, true);
  };

  _proto.showMapIfInvisible = function showMapIfInvisible() {
    if (this.isMapInvisible) {
      this.mapDiv.style.visibility = 'visible';
      this.isMapInvisible = false;
    }
  };

  _proto.removeListeners = function removeListeners() {
    for (var i = 0; i < this.registeredListeners.length; ++i) {
      google.maps.event.removeListener(this.registeredListeners[i]);
    }

    this.registeredListeners = [];
  };

  _proto.registerHandlers = function registerHandlers() {
    if (this.gmap) {
      this.registeredListeners.push(this.gmap.addListener('bounds_changed', this.onBoundsChange));
      this.registeredListeners.push(this.gmap.addListener('dragstart', this.onDragStart));
      this.registeredListeners.push(this.gmap.addListener('idle', this.onIdle));
      this.registeredListeners.push(this.gmap.addListener('maptypeid_changed', this.onMapTypeIdChanged));
      this.registeredListeners.push(this.gmap.addListener('zoom_changed', this.onZoomChanged));
      this.registeredListeners.push(google.maps.event.addListener(this.gmap.getStreetView(), 'visible_changed', this.onStreetViewChanged)); // currently unused events, but adding logging handlers for debugging purposes

      if (this.heavyEventTracking) {
        this.registeredListeners.push(this.gmap.addListener('center_changed', this.generateUnusedGoogleEventLogFunction('center_changed')));
        this.registeredListeners.push(this.gmap.addListener('dblclick', this.generateUnusedGoogleEventLogFunction('dblclick')));
        this.registeredListeners.push(this.gmap.addListener('drag', this.generateUnusedGoogleEventLogFunction('drag')));
        this.registeredListeners.push(this.gmap.addListener('dragend', this.generateUnusedGoogleEventLogFunction('dragend')));
        this.registeredListeners.push(this.gmap.addListener('heading_changed', this.generateUnusedGoogleEventLogFunction('heading_changed')));
        this.registeredListeners.push(this.gmap.addListener('mousemove', this.generateUnusedGoogleEventLogFunction('mousemove')));
        this.registeredListeners.push(this.gmap.addListener('mouseout', this.generateUnusedGoogleEventLogFunction('mouseout')));
        this.registeredListeners.push(this.gmap.addListener('mouseover', this.generateUnusedGoogleEventLogFunction('mouseover')));
        this.registeredListeners.push(this.gmap.addListener('projection_changed', this.generateUnusedGoogleEventLogFunction('projection_changed')));
        this.registeredListeners.push(this.gmap.addListener('resize', this.generateUnusedGoogleEventLogFunction('resize')));
        this.registeredListeners.push(this.gmap.addListener('rightclick', this.generateUnusedGoogleEventLogFunction('rightclick')));
        this.registeredListeners.push(this.gmap.addListener('tilesloaded', this.generateUnusedGoogleEventLogFunction('tilesloaded')));
        this.registeredListeners.push(this.gmap.addListener('tilt_changed', this.generateUnusedGoogleEventLogFunction('tilt_changed')));
      }
    }
  };

  _proto.syncGMap = function syncGMap(ns) {
    var nextState = _objectSpread({}, ns); // Only sync the map if there is one.


    if (this.gmap !== null) {
      // Set flag to disable event handling from the map.
      // we shouldn't fire change events when directed externally to apply changes
      // to avoid infinite loops of update cycles between map and props.
      this.supressMapEventHandling = true; // If our state has a bounding react, compare
      // with the maps current bounding rect.

      if (nextState.boundingRect) {
        var newBounds = {
          east: nextState.boundingRect.eastLng,
          north: nextState.boundingRect.northLat,
          south: nextState.boundingRect.southLat,
          west: nextState.boundingRect.westLng
        };
        var newPadding = {
          top: (0, _get2.default)(nextState, 'boundingPadding.top', 0),
          bottom: (0, _get2.default)(nextState, 'boundingPadding.bottom', 0),
          right: (0, _get2.default)(nextState, 'boundingPadding.right', 0),
          left: (0, _get2.default)(nextState, 'boundingPadding.left', 0)
        }; // Cache the current bounds so we don't have to keep asking gmap.
        // use last reported bounds if available so we don't override map updates
        // that have not been reported yet.

        var currentBounds = this.state.lastReportedBounds.bounds || this.gmap.getBounds(); // Check the format of current bounds.
        // There are edge cases where sometimes google map doesn't always return
        // the same type of bounds object.

        if (currentBounds && currentBounds.north === undefined && currentBounds.getNorthEast) {
          currentBounds = {
            north: currentBounds.getNorthEast().lat(),
            south: currentBounds.getSouthWest().lat(),
            east: currentBounds.getNorthEast().lng(),
            west: currentBounds.getSouthWest().lng()
          };
        } // Only apply changes if there is a meaningful difference.


        if (ZillowMapControl.boundsMinimumDelta(currentBounds, newBounds) || this.fireFirstBoundsChange) {
          this.gmap.fitBounds(newBounds, newPadding);
        }
      } else if (nextState.mapCenter) {
        // Map center is only respected if there isn't a bounding rect.
        // They don't make sense if they are different, and they are redundant if they
        // are not.
        var currentCenter = this.gmap.getCenter();

        if (!currentCenter || currentCenter.lat !== nextState.mapCenter.northSouthLat || currentCenter.lng !== nextState.mapCenter.eastWestLng) {
          this.gmap.setCenter({
            lat: nextState.mapCenter.northSouthLat,
            lng: nextState.mapCenter.eastWestLng
          });
        }
      } // If the zoom level is set, but does not match the map control, then set it.


      if (nextState.zoomLevel) {
        if (this.gmap.getZoom() !== nextState.zoomLevel) {
          this.gmap.setZoom(nextState.zoomLevel);
        }
      }

      var mapType = nextState.mapType;

      if (mapType === 'auto') {
        mapType = this.gmap.getZoom() >= 18 ? 'hybrid' : 'roadmap;';
      }

      if (mapType) {
        if (mapType !== this.gmap.getMapTypeId()) {
          switch (mapType) {
            case 'hybrid':
              this.gmap.setMapTypeId('hybrid');
              break;

            case 'roadmap':
              this.gmap.setMapTypeId('roadmap');
              break;

            case 'satellite':
              this.gmap.setMapTypeId('satellite');
              break;

            case 'terrain':
              this.gmap.setMapTypeId('terrain');
              break;

            default:
              this.gmap.setMapTypeId('roadmap');
              break;
          }
        } // We apply different overall classes to the map to control
        // the colors of things like parcels to make them more visible
        // we do this separtely, because if the map type is different from
        // the default when first loading we want to apply the appropriate styles


        var aerialMapTypes = ['hybrid', 'satellite', 'terrain'];
        var newMapStyleClass = aerialMapTypes.includes(mapType) ? _constants.AERIAL_MAP_CLASS : _constants.STREET_MAP_CLASS;

        if (newMapStyleClass !== this.state.mapStyleClass) {
          this.setState({
            mapStyleClass: newMapStyleClass
          });
        }
      }

      if (nextState.gestureHandling) {
        this.gmap.setOptions({
          gestureHandling: nextState.gestureHandling
        });
      }

      if (nextState.maxZoom !== this.state.maxZoom) {
        this.gmap.setOptions({
          maxZoom: nextState.maxZoom || _constants.DEFAULT_MAXIMUM_ZOOM
        });
      }
    }

    this.supressMapEventHandling = false;
  } // Apply prop changes only if they are new/different.
  // Props and map can get out of sync, and unless the prop is changing
  // the google map is the authority.
  ;

  _proto.applyPropChanges = function applyPropChanges(nextProps, baseProps, isFirstLoad) {
    if (!this.gmap) {
      // If there isn't a google map, no point.
      return null;
    }

    var nextState = _objectSpread({}, baseNextState); // For each prop that drives google map state, check for incoming changes.
    // if changed, process new value.


    if (ZillowMapControl.isNewProp(nextProps, baseProps, 'mapCenter')) {
      nextState.mapCenter = {
        northSouthLat: fromMillionths(nextProps.mapCenter.northSouthLat),
        eastWestLng: fromMillionths(nextProps.mapCenter.eastWestLng)
      };
    }

    if (ZillowMapControl.isNewProp(nextProps, baseProps, 'zoomLevel')) {
      nextState.zoomLevel = nextProps.zoomLevel;
    }

    if (ZillowMapControl.isNewProp(nextProps, baseProps, 'maxZoom')) {
      nextState.maxZoom = nextProps.maxZoom;
    }

    if (this.gmap && ZillowMapControl.isNewProp(nextProps, baseProps, 'updateResizeCount')) {
      // Apply this directly, its not conditional on previous gmap state.
      google.maps.event.trigger(this.gmap, 'resize');
    }

    if (ZillowMapControl.isNewProp(nextProps, baseProps, 'mapType')) {
      nextState.mapType = nextProps.mapType;
    } else if (baseProps.mapType === 'auto' || !baseProps.mapType) {
      nextState.mapType = 'auto';
    }

    if (ZillowMapControl.isNewProp(nextProps, baseProps, 'gestureHandling')) {
      // If we are already freezing the map then don't update the gesture handling.
      // By setting freezeMap to false, we will update to the new gestureHandling values
      // in the next conditional.
      if (!baseProps.freezeMap) {
        nextState.gestureHandling = nextProps.gestureHandling;
      }
    }

    if (ZillowMapControl.isNewProp(nextProps, baseProps, 'freezeMap')) {
      // If we are going to freeze the map, override any gesture handling
      if (nextProps.freezeMap) {
        nextState.gestureHandling = 'none';
      } else {
        // If we are unfreezing the map, get the other gesture handling
        nextState.gestureHandling = nextProps.gestureHandling || baseProps.gestureHandling;
      }
    }

    if (nextProps.boundingRect && ZillowMapControl.boundRectMinimumDelta(nextProps.boundingRect, baseProps.boundingRect) && ZillowMapControl.boundRectMinimumDelta(nextProps.boundingRect, this.state.lastReportedBounds.bounds)) {
      nextState.boundingRect = {
        eastLng: fromMillionths(nextProps.boundingRect.eastLng),
        northLat: fromMillionths(nextProps.boundingRect.northLat),
        southLat: fromMillionths(nextProps.boundingRect.southLat),
        westLng: fromMillionths(nextProps.boundingRect.westLng)
      }; // if bounding box supplied, then don't override the zoom.
      // the map will do its own zoom calculations

      nextState.zoomLevel = null; // if the bounding box is supplied, then we respect bounding padding

      nextState.boundingPadding = nextProps.boundingPadding;
    } // Support late registered ready handlers
    // Mostly for supporting pages wrapping this control from YUI
    // But also valid in React.


    if (!isFirstLoad && nextProps.onReady !== baseProps.onReady && nextProps.onReady && this.state.googleReady) {
      nextProps.onReady();
    } // Override local state with the planned values for the google map.


    this.syncGMap(nextState); // Return next state for unit testing purposes. Disabling return complaint because it's
    // only for testing
    // eslint-disable-next-line consistent-return

    return nextState;
  };

  _proto.render = function render() {
    var context = {
      gmap: this.gmap
    }; // Zillow Map Control owns the google map instance.
    // It provides this instance to child objects via ZillowMapContext.Provider.

    return (// "Children" are most likely either map layers, or map controls.
      // rendering them after the div the map renders into so they will be 'on top'
      _react.default.createElement("div", {
        id: this.props.id,
        className: "zillow-map-control " + this.state.mapStyleClass,
        __source: {
          fileName: _jsxFileName,
          lineNumber: 856
        },
        __self: this
      }, _react.default.createElement(_ZillowMapContext.default.Provider, {
        value: context,
        __source: {
          fileName: _jsxFileName,
          lineNumber: 860
        },
        __self: this
      }, _react.default.createElement(_ConstantDiv.default, {
        className: "map-container",
        style: {
          position: 'absolute',
          width: '100%',
          height: '100%'
        },
        registerChild: this.registerChild // We have the click handler on the div here because the google on click
        // handler is outside of the React event binding, which causes click events
        // for children to break if we the child fires an onClick handler and we
        // remove the child from the react tree during the same setState batch
        // handling.
        ,
        onClick: this.onClick,
        __source: {
          fileName: _jsxFileName,
          lineNumber: 861
        },
        __self: this
      }), this.props.children))
    );
  };

  return ZillowMapControl;
}(_react.Component);

ZillowMapControl.defaultProps = {
  id: '',
  maxZoom: _constants.DEFAULT_MAXIMUM_ZOOM,
  minZoom: _constants.DEFAULT_MINIMUM_ZOOM,
  mapCenter: _constants.DEFAULT_MAP_CENTER,
  useBeta: false,
  boundingRect: null,
  boundingPadding: null,
  zoomLevel: _constants.DEFAULT_ZOOM,
  onBoundsChange: function onBoundsChange() {},
  onReady: function onReady() {},
  onClickMapAnywhere: function onClickMapAnywhere() {},
  freezeMap: false,
  gestureHandling: 'auto',
  updateResizeCount: 0,
  onDragStart: function onDragStart() {},
  onMapTypeIdChanged: function onMapTypeIdChanged() {},
  onZoomChanged: function onZoomChanged() {},
  onStreetViewChanged: function onStreetViewChanged() {},
  streetViewEnabled: false,
  children: [],
  additionalLibraries: []
};
ZillowMapControl.propTypes = process.env.NODE_ENV !== "production" ? {
  apiKey: _propTypes.default.string.isRequired,
  id: _propTypes.default.string,

  /**
   * Sets the maximum zoom value that the map zoom can be set to
   */
  maxZoom: _propTypes.default.number,

  /**
   * Sets the minimum zoom value that the map zoom can be set to.
   */
  minZoom: _propTypes.default.number,

  /* eslint-disable */
  // Bounding rect/padding are used, but not in a way eslint can easily see
  // so disabling lint error.
  boundingRect: _propTypes.default.shape({
    northLat: _propTypes.default.number,
    southLat: _propTypes.default.number,
    eastLng: _propTypes.default.number,
    westLng: _propTypes.default.number
  }),
  boundingPadding: _propTypes.default.shape({
    top: _propTypes.default.number,
    bottom: _propTypes.default.number,
    right: _propTypes.default.number,
    left: _propTypes.default.number
  }),

  /* eslint-enable */
  mapCenter: _propTypes.default.shape({
    northSouthLat: _propTypes.default.number,
    eastWestLng: _propTypes.default.number
  }),

  /**
   * Passing in a zoom level value sets the map to zoom to that value
   */
  zoomLevel: _propTypes.default.number,

  /**
   * Callback whenever the bounds of the map change (zoom or pan)
   */
  onBoundsChange: _propTypes.default.func,

  /**
   * Callback fired once the map is loaded and ready to interact with
   */
  onReady: _propTypes.default.func,

  /**
   * Callback fired when the user clicks on the map
   */
  onClickMapAnywhere: _propTypes.default.func,

  /**
   * Callback fired when the user begins panning the map at all
   */
  onDragStart: _propTypes.default.func,

  /**
   * Callback fired when the type of map (Ariel, Satellite etc)
   */
  onMapTypeIdChanged: _propTypes.default.func,

  /**
   * Callback fired when the map's internal zoom value changes
   */
  onZoomChanged: _propTypes.default.func,
  onStreetViewChanged: _propTypes.default.func,

  /**
   * Determines how the google map handles gestures/page interaction. Values are taken from
   * https://developers.google.com/maps/documentation/javascript/reference/map#MapOptions
   */
  gestureHandling: _propTypes.default.oneOf(['cooperative', 'greedy', 'none', 'auto']),

  /**
   * Proxy for gestureHandling: none. If this is enabled we always set gestureHandling: none,
   * overrides the gestureHandling prop
   */
  // eslint-disable-next-line react/no-unused-prop-types
  freezeMap: _propTypes.default.bool,
  // eslint-disable-next-line react/no-unused-prop-types
  updateResizeCount: _propTypes.default.number,

  /**
   * Prop for development use only. Causes the map to fetch the beta version of the
   * google map library
   */
  useBeta: _propTypes.default.bool,
  // if streetViewEnable is true, google map street view feature will be enabled and the
  // street view icon will be display on the map
  streetViewEnabled: _propTypes.default.bool,

  /**
   * PropType for ensuring children passed in are React nodes
   */
  children: _propTypes.default.oneOfType([_propTypes.default.arrayOf(_propTypes.default.node), _propTypes.default.node]),

  /*
   * PropType for adding additional libraries to the query to Google.
   * ex: additionalLibraries: ['stats', 'places'] would lead to the following query params being appended:
   * https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=stats,places`;
   */
  additionalLibraries: _propTypes.default.array
} : {};
var _default = ZillowMapControl; // eslint-disable-next-line

exports.default = _default;
var _testHooks = {
  fromMillionths: fromMillionths,
  resetInstanceCache: resetInstanceCache
};
exports._testHooks = _testHooks;